import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, XCircle } from "lucide-react";
import { API } from "../config/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success(t('auth.passwordChanged'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes("scaduto") || detail?.includes("expired")) {
        setError(t('auth.tokenExpired'));
      } else {
        setError(detail || t('auth.resetError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <XCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('auth.invalidLink')}</h2>
          <p className="text-gray-600 mb-6">{t('auth.invalidLinkDesc')}</p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="bg-lojack-red text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
          >
            {t('auth.requestNewLink')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <div className="bg-white rounded-lg shadow-lg">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-1 mb-4">
            <span className="text-4xl font-bold text-gray-800">Lo</span>
            <span className="text-4xl font-bold text-lojack-red">/</span>
            <span className="text-4xl font-bold text-gray-800">Jack</span>
          </div>
          <h2 className="text-xl text-gray-600">{t('auth.resetPassword')}</h2>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600" size={32} />
              </div>
              <p className="text-gray-600">{t('auth.passwordChanged')}</p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-lojack-red text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition"
            >
              {t('auth.login')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.newPassword')}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent transition"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red focus:border-transparent transition"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-lojack-red text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="reset-submit"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                t('auth.resetPassword')
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
